import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard, RoleGuard, StandardURL } from "../App"; // Import StandardURL
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faMoneyBillWave,
  faPhone,
  faToggleOn,
  faToggleOff,
  faWallet,
  faCheckCircle,
  faTimesCircle,
  faInfoCircle,
  faDollarSign, // Ensure faDollarSign is imported
} from "@fortawesome/free-solid-svg-icons";
import { Oval } from "react-loader-spinner";



// Set app element for react-modal to avoid accessibility warnings
Modal.setAppElement("#root");

const SMSSetting = () => {
  const navigate = useNavigate();

  const [smsBalance, setSmsBalance] = useState(null);
  const [currentPhoneNumber, setCurrentPhoneNumber] = useState("");
  const [newPhoneNumber, setNewPhoneNumber] = useState(""); // Used for both top-up and update
  const [smsStatus, setSmsStatus] = useState(false); // true for active, false for inactive
  const [topUpAmount, setTopUpAmount] = useState(""); // New state for top-up amount

  const [loadingDetails, setLoadingDetails] = useState(true); // Combined loading for balance, phone, status
  const [loadingTopUp, setLoadingTopUp] = useState(false);
  const [loadingUpdatePhone, setLoadingUpdatePhone] = useState(false);
  const [loadingToggleStatus, setLoadingToggleStatus] = useState(false);

  const [detailsError, setDetailsError] = useState(null); // Combined error for balance, phone, status

  // Correctly fetching propertyId and token from localStorage as per user's instruction
  const propertyId = localStorage.getItem("selectedPropertyId");
  const token = localStorage.getItem("token");

  useEffect(() => {
    const fetchAllSmsSettings = async () => {
      if (!propertyId || !token) {
        setDetailsError("Property not selected or user not authenticated. Please log in.");
        setLoadingDetails(false);
        return;
      }

      try {
        setLoadingDetails(true);
        const response = await axios.get(
          `${StandardURL}/iNyumba/admin/sms/settings/view/${propertyId}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const responseData = response.data.response;
        console.log("SMS Settings fetched:", responseData);

        setSmsBalance(responseData?.smsBalance || "N/A"); // Backend returns 'smsBalance'
        setCurrentPhoneNumber(responseData?.propertyPhoneNumber || ""); // Read 'propertyPhoneNumber' from backend
        setNewPhoneNumber(responseData?.propertyPhoneNumber || "");     // Read 'propertyPhoneNumber' from backend
        setSmsStatus(responseData?.status === "active"); // Ensure case-sensitive match "active" for the status
        
        toast.success("SMS settings fetched successfully!");
      } catch (error) {
        console.error("Error fetching SMS settings:", error);
        setDetailsError(error.response?.data?.response || "Failed to fetch SMS settings.");
        toast.error(error.response?.data?.response || "Failed to fetch SMS details.");
        setSmsBalance("Error");
        setCurrentPhoneNumber("Error");
        setSmsStatus(false); // Default to inactive on error
      } finally {
        setLoadingDetails(false);
      }
    };

    fetchAllSmsSettings();
  }, [propertyId, token]);

  // Handle Top Up SMS
  const handleTopUpSms = async () => {
    if (!propertyId || !token || !newPhoneNumber || !topUpAmount) {
      toast.error("Please enter both amount and phone number, and ensure property is selected and you are logged in.");
      return;
    }
    if (isNaN(topUpAmount) || parseFloat(topUpAmount) <= 0) {
      toast.error("Please enter a valid positive amount for top-up.");
      return;
    }

    setLoadingTopUp(true);
    try {
      const response = await axios.post( // Changed to axios.post as per user's latest instruction
        `${StandardURL}/iNyumba/stkpushsmstopup/${propertyId}`, // Conclusive endpoint as per user's latest instruction
        {
          amount: parseFloat(topUpAmount), // Send amount
          phone_number: newPhoneNumber, // Send phone_number
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      toast.success(response.data.response || "STK push initiated successfully!");
      // Optionally clear amount after successful initiation, as balance isn't updated instantly
      setTopUpAmount("");
      // You might want to re-fetch balance after a short delay or based on a webhook
      // For now, we only re-fetch if the user explicitly refreshes or another action triggers fetchAllSmsSettings
    } catch (error) {
      console.error("Error topping up SMS:", error);
      toast.error(error.response?.data?.response || "Failed to initiate STK push.");
    } finally {
      setLoadingTopUp(false);
    }
  };

  // Handle Update Phone Number
  const handleUpdatePhoneNumber = async () => {
    if (!propertyId || !token || !newPhoneNumber) {
      toast.error("Please enter a new phone number.");
      return;
    }
    setLoadingUpdatePhone(true);
    try {
      const response = await axios.patch(
        `${StandardURL}/iNyumba/admin/sms/settings/phonenumber/update/${propertyId}`, // Conclusive endpoint
        { phonenumber: newPhoneNumber },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setCurrentPhoneNumber(newPhoneNumber); // Update displayed number
      toast.success(response.data.response || "Phone number updated successfully!");
    } catch (error) {
      console.error("Error updating phone number:", error);
      toast.error(error.response?.data?.response || "Failed to update phone number.");
    } finally {
      setLoadingUpdatePhone(false);
    }
  };

  // Handle Toggle SMS Status
  const handleToggleSmsStatus = async () => {
    if (!propertyId || !token) {
      toast.error("Property not selected or user not authenticated.");
      return;
    }
    setLoadingToggleStatus(true);
    const newStatus = !smsStatus; // Toggle the current status
    try {
      const response = await axios.patch(
        `${StandardURL}/iNyumba/admin/sms/settings/status/${propertyId}`, // Conclusive endpoint
        null, // Sending null/empty body as per backend instruction for toggle
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSmsStatus(newStatus); // Update local state on success
      toast.success(response.data.response || `SMS status updated to ${newStatus ? "ACTIVE" : "INACTIVE"}.`);
    } catch (error) {
      console.error("Error toggling SMS status:", error);
      toast.error(error.response?.data?.response || "Failed to update SMS status.");
    } finally {
      setLoadingToggleStatus(false);
    }
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-lg p-6 sm:p-8 lg:p-10">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-8 border-b pb-4">
              <button
                onClick={() => navigate("/landlord/home")}
                className="flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200 mb-4 sm:mb-0"
              >
                <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                Back to Dashboard
              </button>
              <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 text-center flex-grow">
                <FontAwesomeIcon icon={faMoneyBillWave} className="mr-3 text-indigo-600" />
                SMS Settings
              </h1>
            </div>

            {/* SMS Balance Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8 shadow-sm">
              <h2 className="text-2xl font-bold text-blue-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faWallet} className="mr-3 text-blue-600" />
                SMS Balance
              </h2>
              {loadingDetails ? ( // Using loadingDetails for balance
                <div className="flex items-center justify-center py-4">
                  <Oval height={30} width={30} color="#4F46E5" secondaryColor="#C7D2FE" strokeWidth={4} strokeWidthSecondary={4} />
                  <p className="ml-3 text-lg text-gray-700">Fetching balance...</p>
                </div>
              ) : detailsError ? ( // Using detailsError for balance
                <div className="text-red-700 bg-red-100 p-3 rounded-md flex items-center">
                  <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                  <p>{detailsError}</p>
                </div>
              ) : (
                <p className="text-4xl font-extrabold text-blue-700 text-center">
                  <span className="text-2xl align-top mr-1">KSH</span> {smsBalance !== null ? parseFloat(smsBalance).toLocaleString() : "N/A"}
                </p>
              )}
            </div>

            {/* Top Up SMS Section */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mb-8 shadow-sm">
              <h2 className="text-2xl font-bold text-purple-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faMoneyBillWave} className="mr-3 text-purple-600" />
                Top Up SMS
              </h2>
              <p className="text-gray-600 mb-4">Enter the M-Pesa registered phone number and amount to receive an STK push for topping up your SMS balance.</p>
              <div className="flex flex-col sm:flex-row gap-4 mb-4"> {/* Added mb-4 for spacing */}
                <div className="flex-grow">
                  <label htmlFor="topUpAmount" className="sr-only">Amount</label>
                  <div className="relative">
  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none space-x-1">
    <FontAwesomeIcon icon={faMoneyBillWave} className="text-gray-400" />
    <span className="text-gray-400 text-sm">KSh</span>
  </div>
  <input
    id="topUpAmount"
    type="number"
    placeholder="Enter amount (e.g., 100)"
    value={topUpAmount}
    onChange={(e) => setTopUpAmount(e.target.value)}
    className="w-full pl-20 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
    disabled={loadingTopUp}
    min="0"
    step="0.01"
  />
</div>

                </div>
                <div className="flex-grow">
                  <label htmlFor="topUpPhoneNumber" className="sr-only">Phone Number</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faPhone} className="text-gray-400" />
                    </div>
                    <input
                      id="topUpPhoneNumber"
                      type="text"
                      placeholder="Enter phone number (e.g., 2547XXXXXXXX)"
                      value={newPhoneNumber}
                      onChange={(e) => setNewPhoneNumber(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500"
                      disabled={loadingTopUp}
                    />
                  </div>
                </div>
              </div>
              <button
                onClick={handleTopUpSms}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2.5 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loadingTopUp || !newPhoneNumber || !topUpAmount || isNaN(parseFloat(topUpAmount)) || parseFloat(topUpAmount) <= 0}
              >
                {loadingTopUp ? (
                  <Oval height={20} width={20} color="#FFF" secondaryColor="#D8BFD8" strokeWidth={5} strokeWidthSecondary={5} />
                ) : (
                  <><FontAwesomeIcon icon={faMoneyBillWave} className="mr-2" /> Send STK Push</>
                )}
              </button>
            </div>

            {/* Update Phone Number Section */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 mb-8 shadow-sm">
              <h2 className="text-2xl font-bold text-yellow-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={faPhone} className="mr-3 text-yellow-600" />
                Update Registered Phone Number
              </h2>
              {loadingDetails ? ( // Using loadingDetails for phone number
                <div className="flex items-center justify-center py-4">
                  <Oval height={30} width={30} color="#F59E0B" secondaryColor="#FEF3C7" strokeWidth={4} strokeWidthSecondary={4} />
                  <p className="ml-3 text-lg text-gray-700">Loading current phone number...</p>
                </div>
              ) : detailsError ? ( // Using detailsError for phone number
                <div className="text-red-700 bg-red-100 p-3 rounded-md flex items-center mb-4">
                  <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                  <p>{detailsError}</p>
                </div>
              ) : (
                <p className="text-gray-700 mb-4">
                  Current Registered Phone Number: <span className="font-semibold">{currentPhoneNumber || "Not Set"}</span>
                </p>
              )}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-grow">
                  <label htmlFor="updatePhoneNumberInput" className="sr-only">New Phone Number</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FontAwesomeIcon icon={faPhone} className="text-gray-400" />
                    </div>
                    <input
                      id="updatePhoneNumberInput"
                      type="text"
                      placeholder="Enter new phone number (e.g., 2547XXXXXXXX)"
                      value={newPhoneNumber}
                      onChange={(e) => setNewPhoneNumber(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-yellow-500 focus:border-yellow-500"
                      disabled={loadingUpdatePhone}
                    />
                  </div>
                </div>
                <button
                  onClick={handleUpdatePhoneNumber}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold py-2.5 px-6 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={loadingUpdatePhone || !newPhoneNumber || newPhoneNumber === currentPhoneNumber}
                >
                  {loadingUpdatePhone ? (
                    <Oval height={20} width={20} color="#FFF" secondaryColor="#FDF6E3" strokeWidth={5} strokeWidthSecondary={5} />
                  ) : (
                    <><FontAwesomeIcon icon={faPhone} className="mr-2" /> Update Phone Number</>
                  )}
                </button>
              </div>
            </div>

            {/* Update SMS Status Section */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 shadow-sm">
              <h2 className="text-2xl font-bold text-green-800 mb-4 flex items-center">
                <FontAwesomeIcon icon={smsStatus ? faToggleOn : faToggleOff} className={`mr-3 ${smsStatus ? "text-green-600" : "text-gray-500"}`} />
                SMS Sending Status
              </h2>
              {loadingDetails ? ( // Using loadingDetails for status
                <div className="flex items-center justify-center py-4">
                  <Oval height={30} width={30} color="#10B981" secondaryColor="#D1FAE5" strokeWidth={4} strokeWidthSecondary={4} />
                  <p className="ml-3 text-lg text-gray-700">Loading SMS status...</p>
                </div>
              ) : detailsError ? ( // Using detailsError for status
                <div className="text-red-700 bg-red-100 p-3 rounded-md flex items-center mb-4">
                  <FontAwesomeIcon icon={faTimesCircle} className="mr-2" />
                  <p>{detailsError}</p>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-lg font-semibold text-gray-700">
                    Status:{" "}
                    <span className={smsStatus ? "text-green-600" : "text-red-600"}>
                      {smsStatus ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </p>
                  <button
                    onClick={handleToggleSmsStatus}
                    className={`px-6 py-2.5 rounded-lg shadow-md transition-all duration-300 flex items-center justify-center ${
                      smsStatus
                        ? "bg-red-600 hover:bg-red-700 text-white"
                        : "bg-green-600 hover:bg-green-700 text-white"
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                    disabled={loadingToggleStatus}
                  >
                    {loadingToggleStatus ? (
                      <Oval height={20} width={20} color="#FFF" secondaryColor="#CCFBF1" strokeWidth={5} strokeWidthSecondary={5} />
                    ) : (
                      <>
                        <FontAwesomeIcon icon={smsStatus ? faToggleOff : faToggleOn} className="mr-2" />
                        {smsStatus ? "Deactivate SMS" : "Activate SMS"}
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default SMSSetting;